import { Suspense } from 'react'

import { createFileRoute, Link, stripSearchParams } from '@tanstack/react-router'

import Text from '@ingka/text'

import { Footer } from '@/components/footer'
import { SkipButton } from '@/components/skip-button'
import { StickyHeader } from '@/components/sticky-header'
import { dataTestIds } from '@/constants/selectors'
import {
  Filter,
  StartGallery,
  StartGallerySkeleton,
  setFilterInSessionStorage,
  startGalleryFilterSchema,
  startGalleryFilterSchemaDefaultValues,
} from '@/features/start-gallery'
import { useTranslate } from '@/hooks/use-translate'
import { getUserFlowBasedOnPlatform } from '@/integrations/insights/user-flow'
import { getPlatform } from '@/utils/get-platform'

import styles from './index.module.css'

export const Route = createFileRoute('/$country/$language/navigator/')({
  validateSearch: startGalleryFilterSchema,
  component: IndexRoute,
  search: {
    middlewares: [stripSearchParams(startGalleryFilterSchemaDefaultValues)],
  },
  beforeLoad: ({ search, context, cause }) => {
    setFilterInSessionStorage(search)
    if (cause === 'enter') {
      const platform = getPlatform(search)
      const { flowName, availableSteps } = getUserFlowBasedOnPlatform(platform)
      void context.insightsApi?.sendUserFlowStep(flowName, availableSteps.LANDING_PAGE, 1)
      context.sceneHistory.clear()
    }
  },
})

function IndexRoute() {
  const { translateString } = useTranslate()
  const { dev: isDev } = Route.useSearch()
  return (
    <div className={styles.container}>
      <StickyHeader />
      <main className={styles.main}>
        <div className={styles.content}>
          <div className={styles.titleContainer}>
            <Text data-testid={dataTestIds.landing.startPageIntroTitle} tagName="h1" textStyle="Display.L">
              {translateString('startpage.intro.title')}
            </Text>
            <Text tagName="p" textStyle="Body.L">
              {translateString('startpage.intro.body')}
            </Text>
            {isDev && (
              <Text tagName="p" textStyle="Body.L">
                See the current brygga stats{' '}
                <Link from="/$country/$language/navigator/" to="/$country/$language/brygga">
                  here
                </Link>
              </Text>
            )}
          </div>
          <Filter />
          <SkipButton
            label={translateString('common.skip_list').replace(
              '{list_type}',
              translateString('startpage.intro.title').toLowerCase(),
            )}
            targetId="after-gallery-sentinel"
          />
          <ul className={styles.cardList} data-testid={dataTestIds.landing.startPageGallery}>
            <Suspense fallback={<StartGallerySkeleton />}>
              <StartGallery />
            </Suspense>
          </ul>
          <span aria-hidden="true" id="after-gallery-sentinel" tabIndex={-1} />
        </div>
      </main>
      <Footer />
    </div>
  )
}
